import { motion } from 'framer-motion'
import type { RunHistoryItem } from '../types'
import { formatProbability, formatTimeAgo } from '../utils/format'
import clsx from 'clsx'

interface RunTimelineProps {
  runs: RunHistoryItem[]
  selectedRunId: number | null
  onSelectRun: (runId: number) => void
}

export function RunTimeline({ runs, selectedRunId, onSelectRun }: RunTimelineProps) {
  if (runs.length === 0) {
    return (
      <div className="text-center py-8 text-star-muted font-mono text-sm">
        No prediction runs yet
      </div>
    )
  }
  
  return (
    <div className="space-y-2">
      {runs.map((run, index) => {
        const isSelected = run.run_id === selectedRunId
        
        return (
          <motion.button
            key={run.run_id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.05 }}
            className={clsx(
              'w-full text-left p-3 rounded-lg transition-all duration-200 flex items-center gap-4',
              isSelected
                ? 'bg-signal-blue/10 border border-signal-blue/30'
                : 'bg-void-50 border border-transparent hover:border-star-dim/30'
            )}
            onClick={() => onSelectRun(run.run_id)}
          >
            {/* Timeline dot */}
            <div className="relative flex-shrink-0">
              <div className={clsx(
                'w-3 h-3 rounded-full transition-colors',
                isSelected ? 'bg-signal-blue' : 'bg-star-dim'
              )} />
              {/* Connecting line */}
              {index < runs.length - 1 && (
                <div className="absolute top-3 left-1/2 -translate-x-1/2 w-px h-8 bg-star-dim/30" />
              )}
            </div>
            
            {/* Run info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-4">
                <span className="font-mono text-sm text-star">
                  Run #{run.run_id}
                </span>
                <span className="text-xs text-star-muted font-mono">
                  {formatTimeAgo(run.started_at)}
                </span>
              </div>
              
              <div className="flex items-center gap-4 mt-1">
                {run.post_prob !== undefined && (
                  <span className="text-xs font-mono">
                    <span className="text-star-muted">P:</span>{' '}
                    <span className="text-star">{formatProbability(run.post_prob)}</span>
                  </span>
                )}
                {run.pre_prob !== undefined && run.post_prob !== undefined && (
                  <span className={clsx(
                    'text-xs font-mono',
                    run.post_prob > run.pre_prob ? 'text-signal-green' : 'text-signal-red'
                  )}>
                    {run.post_prob > run.pre_prob ? '↑' : '↓'}{' '}
                    {Math.abs((run.post_prob - run.pre_prob) * 100).toFixed(1)}%
                  </span>
                )}
              </div>
            </div>
            
            {/* Probability indicator */}
            {run.post_prob !== undefined && (
              <div className="w-16 h-2 bg-void-200 rounded-full overflow-hidden flex-shrink-0">
                <motion.div
                  className="h-full bg-gradient-to-r from-star-muted to-star"
                  initial={{ width: 0 }}
                  animate={{ width: `${run.post_prob * 100}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
            )}
          </motion.button>
        )
      })}
    </div>
  )
}


